import { ReactNode, useEffect, useRef } from "react";

import {
	AppShell,
	Box,
	Burger,
	Group,
	Image,
	LoadingOverlay,
	Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import logo from "@/Assets/images/logo.png";
import { useAudioPlayer } from "@/audio/AudioPlayerProvider";
import { useAuth } from "@/Components/Auth/AuthProvider";
import SideBar from "@/Components/Layout/SideBar";
import MiniPlayer from "@/Components/Player/MiniPlayer";
import { usePageTracking } from "@/config/analytics";
import { Config } from "@/config/Config";
import { isLoginPath, isPublicPath } from "@/Router";

export default function MainLayout({ children }: { children: ReactNode }) {
	const location = useLocation();
	const { t } = useTranslation();
	const { isAuthenticated, isLoading, registerStopPlayer } = useAuth();
	const audioPlayer = useAudioPlayer();
	const [navOpened, { toggle: toggleNav, close: closeNav }] =
		useDisclosure(false);

	usePageTracking();

	// Stop playback on every navigation (but not on the initial mount).
	const stopRef = useRef(audioPlayer.stop);
	stopRef.current = audioPlayer.stop;
	const isFirstNavigation = useRef(true);
	useEffect(() => {
		if (isFirstNavigation.current) {
			isFirstNavigation.current = false;
			return;
		}
		stopRef.current();
	}, [location.pathname]);

	// Auth token swaps (login/impersonation) stop the player too.
	useEffect(() => {
		registerStopPlayer(() => stopRef.current());
	}, [registerStopPlayer]);

	const showChrome =
		isAuthenticated &&
		!isLoginPath(location.pathname) &&
		!isPublicPath(location.pathname) &&
		!Config.maintenance_mode;

	if (!showChrome) {
		return (
			<>
				<LoadingOverlay
					visible={isLoading}
					zIndex={99999}
					overlayProps={{ blur: 1 }}
				/>
				{children}
				<MiniPlayer />
			</>
		);
	}

	return (
		<AppShell
			navbar={{
				width: 238,
				breakpoint: "sm",
				collapsed: { mobile: !navOpened },
			}}
			styles={{
				navbar: {
					backgroundColor: "var(--hooky-sidebar)",
					borderColor: "var(--hooky-border)",
				},
			}}
		>
			<AppShell.Navbar>
				<SideBar onNavigate={closeNav} />
			</AppShell.Navbar>
			<AppShell.Main>
				{/* Mobile top bar (mockup .mtop): a sticky brand row with the
				    navbar burger. Replaces a floating burger that overlapped the
				    page header actions. Hidden on desktop where the sidebar is
				    always visible. */}
				<Group
					hiddenFrom="sm"
					h={52}
					px="md"
					gap="sm"
					wrap="nowrap"
					pos="sticky"
					top={0}
					style={{
						zIndex: 100,
						backgroundColor: "var(--hooky-sidebar)",
						borderBottom: "1px solid var(--hooky-border)",
					}}
				>
					<Burger
						opened={navOpened}
						onClick={toggleNav}
						size="sm"
						aria-label={t("Toggle navigation")}
					/>
					<Image src={logo} alt="logo" h={28} w="auto" radius="sm" />
					<Text fw={700} fz="lg">
						Hooky
					</Text>
				</Group>
				{/* Dimmed backdrop while the mobile drawer is open; tapping it
				    closes the navbar (the mockup .sbback). */}
				{navOpened && (
					<Box
						hiddenFrom="sm"
						onClick={closeNav}
						pos="fixed"
						inset={0}
						style={{
							zIndex: 100,
							background: "rgba(5, 9, 15, 0.6)",
						}}
					/>
				)}
				<LoadingOverlay
					visible={isLoading}
					zIndex={99999}
					overlayProps={{ blur: 1 }}
				/>
				{children}
			</AppShell.Main>
			<MiniPlayer />
		</AppShell>
	);
}
